/* eslint-disable prettier/prettier */

export interface MiType {
  miTypeID: number;
  miType: string;
}

export type MIRowStatus =
  | 'idle'
  | 'loading'
  | 'loading-apr'
  | 'complete'
  | 'error';

export type DisplayEstimatedMI = 'none' | 'highest' | 'median' | 'lowest';

export interface MIError {
  toastMessage: string;
  tooltipMessage: string;
}

export const DEFAULT_MI_ERROR: MIError = {
  toastMessage: 'A system error prevented this request from completing. For full error details, open the MI Services Order.',
  tooltipMessage: 'Detailed error found on MI Services',
};

export type MIRowState = {
  status: MIRowStatus;
  miAmount?: number | null;
  aprValue?: number | null;
  error?: Error;
  miError?: MIError;
};

const DEFAULT_ROW_STATE: MIRowState = { status: 'idle' };

export class MIStore {
  private rowStates = new Map<string, MIRowState>();
  private listeners = new Map<string, Set<() => void>>();
  private globalListeners = new Set<() => void>();
  private version = 0;

  getRowState(key: string): MIRowState {
    return this.rowStates.get(key) ?? DEFAULT_ROW_STATE;
  }

  getVersion(): number {
    return this.version;
  }

  updateRowState(key: string, partial: Partial<MIRowState>): void {
    const current = this.getRowState(key);
    this.rowStates.set(key, { ...current, ...partial });
    this.version += 1;
    this.listeners.get(key)?.forEach((cb) => cb());
    this.globalListeners.forEach((cb) => cb());
  }

  subscribeKey(key: string, listener: () => void): () => void {
    if (!this.listeners.has(key)) {
      this.listeners.set(key, new Set());
    }
    this.listeners.get(key)!.add(listener);
    return () => {
      this.listeners.get(key)?.delete(listener);
    };
  }

  subscribeAll(listener: () => void): () => void {
    this.globalListeners.add(listener);
    return () => {
      this.globalListeners.delete(listener);
    };
  }

  reset(): void {
    this.rowStates.clear();
    this.version += 1;
    this.listeners.forEach((keyListeners) => keyListeners.forEach((cb) => cb()));
    this.globalListeners.forEach((cb) => cb());
  }

  dispose(): void {
    this.rowStates.clear();
    this.listeners.clear();
    this.globalListeners.clear();
  }
}

// Used by PMT and EST MI cells — clears after phase 1 (MI call) completes
export const isMIFirstPhaseLoading = (status: MIRowStatus): boolean =>
  status === 'loading';

// Used by APR cell — stays true through phase 2 (APR call)
export const isMILoading = (status: MIRowStatus): boolean =>
  status === 'loading' || status === 'loading-apr';

export const isMIError = (status: MIRowStatus): boolean => status === 'error';

type MITypeConfig = {
  productDescriptions: string[];
  sequenceType: string;
};

const MI_TYPE_CONFIG: Record<string, MITypeConfig> = {
  Monthly:          { productDescriptions: ['BPMI Monthly'],                           sequenceType: 'Second' },
  'Single Payment': { productDescriptions: ['BPMI Single'],                            sequenceType: 'First'  },
  Lender:           { productDescriptions: ['LPMI Single'],                            sequenceType: 'First'  },
  Split:            { productDescriptions: ['SplitPremium', 'BPMI buydown Premium'],   sequenceType: 'First'  },
};

export const computeMIAmount = (
  result: any,
  displayEstimatedMI: DisplayEstimatedMI,
  selectedMiType: string,
): number | null => {
  if (!Array.isArray(result) || !displayEstimatedMI || !selectedMiType) return null;

  const config = MI_TYPE_CONFIG[selectedMiType];
  if (!config) return null;

  const amounts = result
    .flatMap((provider) => provider?.quotes ?? [])
    .filter((quote) => config.productDescriptions.includes(quote?.productDescription))
    .flatMap((quote) => quote?.premiumDetails?.premiums ?? [])
    .filter((premium) => premium?.sequenceType === config.sequenceType)
    .map((premium) => premium?.blendedPaymentAndTax?.amount)
    .filter((amount): amount is number => typeof amount === 'number')
    .sort((a, b) => a - b);

  if (amounts.length === 0) return null;

  const pickRandom = (candidates: number[]): number =>
    candidates[Math.floor(Math.random() * candidates.length)];

  if (displayEstimatedMI === 'highest') {
    const max = amounts[amounts.length - 1];
    return pickRandom(amounts.filter((a) => a === max));
  }
  if (displayEstimatedMI === 'median') {
    const medianValue = amounts[Math.floor((amounts.length - 1) / 2)];
    return pickRandom(amounts.filter((a) => a === medianValue));
  }
  if (displayEstimatedMI === 'lowest') {
    const min = amounts[0];
    return pickRandom(amounts.filter((a) => a === min));
  }

  return null;
};

const MI_TYPE_DISPLAY_ORDER = ['Monthly', 'Single Payment', 'Lender', 'Split'];
const MI_TYPE_LABELS: Record<string, string> = {
  Monthly:          'Monthly',
  'Single Payment': 'Single',
  Lender:           'LPMI',
  Split:            'Split',
};

export const getMIDropdownOptions = (
  miTypes: MiType[],
) =>
  [...miTypes]
    .sort(
      (a, b) =>
        MI_TYPE_DISPLAY_ORDER.indexOf(a.miType) -
        MI_TYPE_DISPLAY_ORDER.indexOf(b.miType),
    )
    .map(({ miType }) => {
      const label = MI_TYPE_LABELS[miType] ?? miType;
      return {
        dsId:  miType,
        type:  'activable-item' as const,
        label,
        value: miType,
      };
    });

export const extractAPRFromResponse = (
  response: any,
  rateDataID: number,
): number | null => {
  const program = response?.loanPrograms?.[0];
  const rateDetail = program?.rateDetails?.find(
    (r: any) => r.rateDataID === rateDataID,
  );
  return rateDetail?.apr ?? null;
};
